// ----------------------------------------------------------------------------
// Snippet: Cart Upsells
// ----------------------------------------------------------------------------
//
// Curated, single-variant upsell products rendered inside the cart with the
// shared product card. Becomes a slider when the cards overflow the viewport
// (desktop: > 4, tablet: > 2, mobile: > 1) and left-aligns when they fit.
// Listens for cart changes so a product added from here is removed from the list.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------
import Swiper from 'swiper';
import { Navigation, Pagination, A11y, Keyboard, Manipulation } from 'swiper/modules';
import 'snippets/card/card';
import 'snippets/icon/icon';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';

// Style includes
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import './_cart-upsells.scss';

// -------------------------------------
// Variables
// -------------------------------------
var CAROUSEL_SELECTOR = '[data-carousel]';
var PREV_BUTTON_SELECTOR = '.swiper-button-prev';
var NEXT_BUTTON_SELECTOR = '.swiper-button-next';
var PAGINATION_SELECTOR = '[data-pagination]';
class iamCartUpsells extends HTMLElement {
  connectedCallback() {
    this.carousel = this.querySelector(CAROUSEL_SELECTOR);
    if (!this.carousel) return;
    this.initSlider();
    this.onCartModifiedRef = this.onCartModified.bind(this);
    iamotaEventHandler.subscribe('cart:modified', this.onCartModifiedRef, {
      source: "cart-upsells.js"
    });
  }
  initSlider() {
    this.swiper = new Swiper(this.carousel, {
      // Manipulation provides removeSlide() (used on add to cart).
      modules: [Navigation, Pagination, A11y, Keyboard, Manipulation],
      // Slide widths come from CSS; 'auto' measures them so scroll bounds
      // match real content and the row can't be dragged off-screen.
      slidesPerView: 'auto',
      spaceBetween: 8,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      a11y: false,
      resistanceRatio: 0,
      // no elastic over-drag past the edges
      pagination: {
        el: this.carousel.querySelector(PAGINATION_SELECTOR),
        type: 'progressbar'
      },
      navigation: {
        nextEl: this.carousel.querySelector(NEXT_BUTTON_SELECTOR),
        prevEl: this.carousel.querySelector(PREV_BUTTON_SELECTOR)
      }
    });
    this.refresh();

    // Re-evaluate on any later width change (the cart's two-column flex and
    // async order-summary render can resize the column after init).
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this.carousel);
    }
  }

  /**
   * Re-measure after a width change or slide removal; watchOverflow re-locks
   * the slider when the cards all fit.
   */
  refresh() {
    if (!this.swiper) return;
    this.swiper.update();
  }

  /**
   * When the cart changes, drop any upsell slide whose product is now in the
   * cart. If none remain, remove the whole section.
   */
  onCartModified(cart) {
    var items = cart === null || cart === void 0 ? void 0 : cart.items;
    if (!Array.isArray(items) || !this.swiper) return;
    var cartProductIds = new Set(items.map(item => String(item.product_id)));

    // Use Swiper's removeSlide() so its cache/index/layout stay in sync;
    // a raw DOM removal leaves stale measurements that make the row jump.
    var indexesToRemove = this.swiper.slides.reduce((indexes, slide, index) => {
      if (cartProductIds.has(slide.dataset.productId)) indexes.push(index);
      return indexes;
    }, []);
    if (!indexesToRemove.length) return;
    this.swiper.removeSlide(indexesToRemove);
    if (!this.swiper.slides.length) {
      this.remove();
      return;
    }
    this.refresh();
  }
  disconnectedCallback() {
    var _iamotaEventHandler$u, _this$resizeObserver, _this$swiper, _this$swiper$destroy;
    (_iamotaEventHandler$u = iamotaEventHandler.unsubscribe) === null || _iamotaEventHandler$u === void 0 || _iamotaEventHandler$u.call(iamotaEventHandler, 'cart:modified', this.onCartModifiedRef);
    (_this$resizeObserver = this.resizeObserver) === null || _this$resizeObserver === void 0 || _this$resizeObserver.disconnect();
    (_this$swiper = this.swiper) === null || _this$swiper === void 0 || (_this$swiper$destroy = _this$swiper.destroy) === null || _this$swiper$destroy === void 0 || _this$swiper$destroy.call(_this$swiper, true, true);
  }
}
if (!customElements.get('iam-cart-upsells')) {
  customElements.define('iam-cart-upsells', iamCartUpsells);
}